import type mapboxgl from 'mapbox-gl';

/**
 * Applies a floating origin translation to the Mapbox projection matrix in double precision.
 * This is crucial for maintaining alignment with Mapbox's high-precision layers like building extrusions.
 *
 * {@link https://github.com/mapbox/mapbox-gl-js/issues/10108}
 *
 * @param matrix - The 4x4 projection matrix provided by Mapbox (number[16]).
 * @param origin - The Mercator origin used for the vertex offsets.
 * @returns A 4x4 matrix compatible with WebGL uniformMatrix4fv.
 */
export function applyFloatingOriginMatrix(
  matrix: readonly number[],
  origin: mapboxgl.MercatorCoordinate,
): Float32Array {
  const tx = origin.x;
  const ty = origin.y;
  const tz = origin.z;

  const out = new Float32Array(16);

  // Copy rotation and scale (first 3 columns) directly.
  for (let i = 0; i < 12; i += 1) {
    out[i] = matrix[i] ?? 0;
  }

  // Calculate the 4th column (translation) in double precision.
  // Result = WorldMatrix * Translation(Origin)
  out[12] =
    (matrix[0] ?? 0) * tx +
    (matrix[4] ?? 0) * ty +
    (matrix[8] ?? 0) * tz +
    (matrix[12] ?? 0);
  out[13] =
    (matrix[1] ?? 0) * tx +
    (matrix[5] ?? 0) * ty +
    (matrix[9] ?? 0) * tz +
    (matrix[13] ?? 0);
  out[14] =
    (matrix[2] ?? 0) * tx +
    (matrix[6] ?? 0) * ty +
    (matrix[10] ?? 0) * tz +
    (matrix[14] ?? 0);
  out[15] =
    (matrix[3] ?? 0) * tx +
    (matrix[7] ?? 0) * ty +
    (matrix[11] ?? 0) * tz +
    (matrix[15] ?? 0);

  return out;
}

type LocalAxisRotation = {
  axis: readonly [number, number, number];
  center: readonly [number, number, number];
  radians: number;
};

/** Apply a local-space rotation after the floating-origin translation. */
export function applyLocalAxisRotationToMatrix(
  matrix: ArrayLike<number>,
  rotation: LocalAxisRotation,
): Float32Array {
  const [x, y, z] = rotation.axis;
  const length = Math.hypot(x, y, z);
  if (!(length > 0) || !Number.isFinite(rotation.radians)) {
    return new Float32Array(matrix);
  }
  const nx = x / length;
  const ny = y / length;
  const nz = z / length;
  const cosine = Math.cos(rotation.radians);
  const sine = Math.sin(rotation.radians);
  const t = 1 - cosine;

  const r00 = t * nx * nx + cosine;
  const r01 = t * nx * ny - sine * nz;
  const r02 = t * nx * nz + sine * ny;
  const r10 = t * nx * ny + sine * nz;
  const r11 = t * ny * ny + cosine;
  const r12 = t * ny * nz - sine * nx;
  const r20 = t * nx * nz - sine * ny;
  const r21 = t * ny * nz + sine * nx;
  const r22 = t * nz * nz + cosine;
  const [cx, cy, cz] = rotation.center;
  const localRotation = new Float64Array([
    r00,
    r10,
    r20,
    0,
    r01,
    r11,
    r21,
    0,
    r02,
    r12,
    r22,
    0,
    cx - (r00 * cx + r01 * cy + r02 * cz),
    cy - (r10 * cx + r11 * cy + r12 * cz),
    cz - (r20 * cx + r21 * cy + r22 * cz),
    1,
  ]);
  const out = new Float32Array(16);
  for (let column = 0; column < 4; column += 1) {
    for (let row = 0; row < 4; row += 1) {
      let value = 0;
      for (let k = 0; k < 4; k += 1) {
        value +=
          (matrix[k * 4 + row] ?? 0) * (localRotation[column * 4 + k] ?? 0);
      }
      out[column * 4 + row] = value;
    }
  }
  return out;
}

/** Translate projected geometry in normalized screen space after projection. */
export function applyClipSpaceOffsetToMatrix(
  matrix: ArrayLike<number>,
  offset: { xNdc?: number; yNdc?: number },
): Float32Array {
  const xNdc = offset.xNdc ?? 0;
  const yNdc = offset.yNdc ?? 0;
  const out = new Float32Array(matrix);
  for (let column = 0; column < 4; column += 1) {
    const index = column * 4;
    const w = matrix[index + 3] ?? 0;
    out[index] = (matrix[index] ?? 0) + xNdc * w;
    out[index + 1] = (matrix[index + 1] ?? 0) + yNdc * w;
  }
  return out;
}

/** Project a floating-origin point into CSS-pixel screen coordinates. */
export function projectLocalPointToScreen(
  matrix: ArrayLike<number>,
  point: readonly [number, number, number],
  viewport: { width: number; height: number },
): readonly [number, number] | undefined {
  if (!(viewport.width > 0) || !(viewport.height > 0)) return undefined;
  const [x, y, z] = point;
  const clipX =
    (matrix[0] ?? 0) * x +
    (matrix[4] ?? 0) * y +
    (matrix[8] ?? 0) * z +
    (matrix[12] ?? 0);
  const clipY =
    (matrix[1] ?? 0) * x +
    (matrix[5] ?? 0) * y +
    (matrix[9] ?? 0) * z +
    (matrix[13] ?? 0);
  const clipW =
    (matrix[3] ?? 0) * x +
    (matrix[7] ?? 0) * y +
    (matrix[11] ?? 0) * z +
    (matrix[15] ?? 0);
  if (!Number.isFinite(clipW) || Math.abs(clipW) < 1e-12) return undefined;
  const ndcX = clipX / clipW;
  const ndcY = clipY / clipW;
  if (!Number.isFinite(ndcX) || !Number.isFinite(ndcY)) return undefined;
  return [
    ((ndcX + 1) / 2) * viewport.width,
    ((1 - ndcY) / 2) * viewport.height,
  ];
}

export type LocalPoint3D = readonly [number, number, number];

export type LocalRay3D = {
  origin: LocalPoint3D;
  direction: LocalPoint3D;
};

/** Invert a column-major 4x4 matrix with Gauss-Jordan elimination. */
const invertMatrix4 = (matrix: ArrayLike<number>): Float64Array | undefined => {
  if (matrix.length < 16) return undefined;
  const rows = Array.from({ length: 4 }, (_, row) =>
    Array.from({ length: 8 }, (_, column) =>
      column < 4 ? (matrix[column * 4 + row] ?? 0) : column - 4 === row ? 1 : 0,
    ),
  );

  for (let pivotColumn = 0; pivotColumn < 4; pivotColumn += 1) {
    let pivotRow = pivotColumn;
    for (let row = pivotColumn + 1; row < 4; row += 1) {
      if (
        Math.abs(rows[row]?.[pivotColumn] ?? 0) >
        Math.abs(rows[pivotRow]?.[pivotColumn] ?? 0)
      ) {
        pivotRow = row;
      }
    }
    const pivot = rows[pivotRow]?.[pivotColumn] ?? 0;
    if (!Number.isFinite(pivot) || Math.abs(pivot) < 1e-12) return undefined;
    [rows[pivotColumn], rows[pivotRow]] = [
      rows[pivotRow] ?? [],
      rows[pivotColumn] ?? [],
    ];

    const pivotValues = rows[pivotColumn];
    if (!pivotValues) return undefined;
    for (let column = 0; column < 8; column += 1) {
      pivotValues[column] = (pivotValues[column] ?? 0) / pivot;
    }
    for (let row = 0; row < 4; row += 1) {
      if (row === pivotColumn) continue;
      const rowValues = rows[row];
      if (!rowValues) continue;
      const factor = rowValues[pivotColumn] ?? 0;
      for (let column = 0; column < 8; column += 1) {
        const value =
          (rowValues[column] ?? 0) - factor * (pivotValues[column] ?? 0);
        rowValues[column] = value;
      }
    }
  }

  const inverse = new Float64Array(16);
  for (let row = 0; row < 4; row += 1) {
    for (let column = 0; column < 4; column += 1) {
      inverse[column * 4 + row] = rows[row]?.[column + 4] ?? 0;
    }
  }
  return inverse;
};

const transformClipPoint = (
  inverse: ArrayLike<number>,
  clip: readonly [number, number, number, number],
): LocalPoint3D | undefined => {
  const transformed = [0, 0, 0, 0].map((_, row) =>
    clip.reduce(
      (sum, value, column) => sum + (inverse[column * 4 + row] ?? 0) * value,
      0,
    ),
  );
  const w = transformed[3] ?? 0;
  if (!Number.isFinite(w) || Math.abs(w) < 1e-12) return undefined;
  const point = transformed.slice(0, 3).map((value) => value / w);
  return point.every(Number.isFinite)
    ? [point[0] ?? 0, point[1] ?? 0, point[2] ?? 0]
    : undefined;
};

/** Unproject a CSS-pixel screen point through a local projection matrix. */
export function unprojectScreenPointToLocalRay(
  matrix: ArrayLike<number>,
  screenPoint: readonly [number, number],
  viewport: { width: number; height: number },
): LocalRay3D | undefined {
  if (!(viewport.width > 0) || !(viewport.height > 0)) return undefined;
  const inverse = invertMatrix4(matrix);
  if (!inverse) return undefined;
  const ndcX = (screenPoint[0] / viewport.width) * 2 - 1;
  const ndcY = 1 - (screenPoint[1] / viewport.height) * 2;
  const near = transformClipPoint(inverse, [ndcX, ndcY, -1, 1]);
  const far = transformClipPoint(inverse, [ndcX, ndcY, 1, 1]);
  if (!near || !far) return undefined;
  const delta: LocalPoint3D = [
    far[0] - near[0],
    far[1] - near[1],
    far[2] - near[2],
  ];
  const length = Math.hypot(...delta);
  if (!(length > 0) || !Number.isFinite(length)) return undefined;
  return {
    origin: near,
    direction: [delta[0] / length, delta[1] / length, delta[2] / length],
  };
}
